import { useState } from 'react';
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Box,
  Alert,
  Card,
  Radio,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Info } from 'react-feather';
import {
  usersApi,
  DEPARTMENTS,
  USER_ROLES,
  type CreateInternalUserInput,
  type InternalPersona,
} from '../../services/api/users';
import classes from './AddUserModal.module.css';

interface AddUserModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Persona options for radio cards
const PERSONA_OPTIONS: { value: InternalPersona; label: string; description: string }[] = [
  {
    value: 'internal_admin',
    label: 'Admin',
    description: 'Full access to all features including user management and templates',
  },
  {
    value: 'internal_user',
    label: 'Cohort Manager',
    description: 'Manage cohorts, participants, tasks, and view coaches',
  },
];

export function AddUserModal({ opened, onClose, onSuccess }: AddUserModalProps) {
  const [loading, setLoading] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const form = useForm<CreateInternalUserInput>({
    initialValues: {
      email: '',
      first_name: '',
      last_name: '',
      persona: 'internal_user',
      job_title: '',
      department: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email address';
        return null;
      },
      first_name: (value) => (!value?.trim() ? 'First name is required' : null),
      last_name: (value) => (!value?.trim() ? 'Last name is required' : null),
      persona: (value) => (!value ? 'Persona is required' : null),
      job_title: (value) => (!value ? 'Role is required' : null),
      department: (value) => (!value ? 'Department is required' : null),
    },
  });

  // Check email uniqueness on blur
  const handleEmailBlur = async () => {
    const email = form.values.email;
    if (!email || form.errors.email) return;

    setEmailCheckLoading(true);
    try {
      const result = await usersApi.checkEmail(email);
      if (result.exists) {
        form.setFieldError('email', 'A user with this email already exists');
      }
    } catch {
      // Silently ignore check errors
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handleSubmit = async (values: CreateInternalUserInput) => {
    setLoading(true);
    try {
      // Final email check
      const emailCheck = await usersApi.checkEmail(values.email);
      if (emailCheck.exists) {
        form.setFieldError('email', 'A user with this email already exists');
        return;
      }

      await usersApi.create(values);
      form.reset();
      onSuccess();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create user',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add New User"
      size="lg"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Name Row */}
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="John"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>

          {/* Email */}
          <TextInput
            label="Email"
            placeholder="name@medley.io"
            required
            {...form.getInputProps('email')}
            onBlur={handleEmailBlur}
            rightSection={emailCheckLoading ? <Text size="xs" c="dimmed">Checking...</Text> : null}
          />

          {/* Persona Selection - Radio Cards */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Persona <span style={{ color: 'red' }}>*</span>
            </Text>
            <Radio.Group
              value={form.values.persona}
              onChange={(value) => form.setFieldValue('persona', value as InternalPersona)}
            >
              <Stack gap="sm">
                {PERSONA_OPTIONS.map((option) => (
                  <Card
                    key={option.value}
                    withBorder
                    padding="md"
                    className={`${classes.personaCard} ${form.values.persona === option.value ? classes.personaCardSelected : ''}`}
                    onClick={() => form.setFieldValue('persona', option.value)}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Radio value={option.value} />
                      <Box>
                        <Text fw={500} size="sm">{option.label}</Text>
                        <Text size="xs" c="dimmed">{option.description}</Text>
                      </Box>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </Radio.Group>
            {form.errors.persona && (
              <Text size="xs" c="red" mt={4}>{form.errors.persona}</Text>
            )}
          </Box>

          {/* Info Alert */}
          <Alert icon={<Info size={16} />} color="gray" variant="light">
            <Text size="xs">
              Coach, Participant, and Partner users are added through their respective workflows.
            </Text>
          </Alert>

          {/* Role and Department Row */}
          <Group grow>
            <Select
              label="Role"
              placeholder="Select role"
              required
              data={USER_ROLES.map((role) => ({ value: role, label: role }))}
              {...form.getInputProps('job_title')}
              searchable
            />
            <Select
              label="Department"
              placeholder="Select department"
              required
              data={DEPARTMENTS.map((dept) => ({ value: dept, label: dept }))}
              {...form.getInputProps('department')}
            />
          </Group>

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Add User
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default AddUserModal;
